import { LogOut } from "lucide-react";
import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { useTranslation } from "react-i18next";

import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { useAuth } from "@/contexts/AuthContext";

export function OnboardingLayout(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);

  // Force light mode while in onboarding (no dark/system option). Restore the
  // user's previous theme preference when they leave the flow.
  useEffect(() => {
    const previous = theme;
    if (theme !== "light") setTheme("light");
    return () => {
      if (previous && previous !== "light") setTheme(previous);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogoutConfirm = async (): Promise<void> => {
    await logout();
    setIsLogoutDialogOpen(false);
    navigate("/login", { replace: true });
  };

  return (
    <div className="relative h-full overflow-y-auto overflow-x-hidden">
      <button
        type="button"
        onClick={() => setIsLogoutDialogOpen(true)}
        className="fx-onb-signout"
      >
        <LogOut aria-hidden />
        <span>{t("header.signOut")}</span>
      </button>
      <main>
        <Outlet />
      </main>
      <ConfirmationDialog
        open={isLogoutDialogOpen}
        onOpenChange={setIsLogoutDialogOpen}
        title={t("header.logoutDialog.title")}
        description={t("header.logoutDialog.description")}
        confirmText={t("header.logoutDialog.confirm")}
        cancelText={t("header.logoutDialog.cancel")}
        variant="destructive"
        onConfirm={handleLogoutConfirm}
      />
    </div>
  );
}
